// 'g'                => // groups
// 'r'                => // registrations
// 'nd'               => // new_deposits --- new deps
// 'dr'               => // dep_red
// 'nda'              => // new_deposits_amount
// 'fda'              => // first_deposit_amount
// 'ad'               => // all_deposits --- all deps
// 'd'                => // deposits --- amount of all deps
// 'b'                => // bets --- amount of bets
// 'ab'               => // avg_bet
// 'ac'               => // avg_check
// 'anc'              => // avg_new_check
// 'i'                => // income
// 's'                => // spend
// 'cfd'              => //
// 'c'                => // count
// 'roi'              => //
// 'cc'               => // unique_click_count
// 'ucc'              => // click_count
// 'cr'               => // click_reg
// 'dss'              => // DepSum/Spend
// 'bss'              => // BetSum/Spend
// 'sg'               => // second_group => sg,  third_group => tg, fourth_group => fg

import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment'
import { walkParents } from '@/helpers/reports'
import i18n from "@/plugins/vue-i18n"
import { generatePassword } from "@/helpers/functions"
import { numberWithSpaces } from '@/helpers/render-value'
import { formatAsPercentage, formatTrimmedNumber, formatTrimmedNumberPrecise } from '@/helpers/number'

/**
 * Единый источник правды для форматирования числовых метрик во всех отчётах на базе
 * ui-expand-group/ui-total-footer (general-report, general-report-click, cohort-analysis,
 * cohort-analysis-cumulative, payment-invoices, solo-payment-invoices).
 * Подключается через проп :value-mutator — применяется автоматически ко всем колонкам,
 * для которых таблица не задаёт собственный v-slot:[`item.<key>`].
 * Правило форматирования меняется здесь один раз и применяется сразу везде.
 *
 * @param {Object} payload
 * @param {*} payload.value - Исходное значение ячейки.
 * @param {string} payload.key - Ключ метрики (см. легенду сокращений в начале файла).
 * @returns {*} Отформатированное значение для рендера.
 */
export function mutateValueBeforeRender({ value: initValue, key }) {
  if (initValue == null) return ''
  if (typeof initValue !== 'number') return initValue
  let value = ''
  if (initValue > 0 && initValue < 1) {
    value = formatTrimmedNumberPrecise(initValue, { trimTrailingZeros: ['cr', 'dr', 'bss', 'dss'].includes(key) })
  } else {
    value = initValue.toFixed(2)
  }
  const integerFields = ['r', 'nd', 'ad', 'cc', 'ucc', 's', 'bss', 'spend_2', 'new_deposits_2', 'cpa_spend', 'rs_spend', 'total_spend']
  const integerPercentFields = ['dss', 'roi', 'roi_2']
  const trimFields = ['fda', 'nda', 'd', 'b', 'ac', 'i', 'ab', 'anc', 'cfd', 'bonus']
  const percentFields = ['cr', 'dr']
  const returnDashFields = ['sp']
  if (integerFields.includes(key)) return numberWithSpaces(Math.round(initValue))
  if (integerPercentFields.includes(key)) return numberWithSpaces(Math.round(initValue)) + '%'
  if (trimFields.includes(key)) return numberWithSpaces(formatTrimmedNumber(Number(value)))
  if (percentFields.includes(key)) return !value && value !== 0 ? '' : formatAsPercentage(value)
  if (returnDashFields.includes(key)) return '-'
  return value
}

// Колонки с цветным лейблом ReportMetricLabel: ключ => доп. ширина рядом с лейблом (стрелка у roi_2 в CA)
const METRIC_LABEL_COLUMNS = { roi: 0, dss: 0, bss: 0, roi_2: 22 }
// padding ячейки 8+8, рамка bss 2+2, воздух по краям лейбла
const METRIC_LABEL_EXTRA_WIDTH = 32
let metricLabelCanvasContext = null

function measureMetricLabelText(text) {
  if (!metricLabelCanvasContext) {
    metricLabelCanvasContext = document.createElement('canvas').getContext('2d')
    metricLabelCanvasContext.font = `500 12px ${getComputedStyle(document.body).fontFamily}`
  }
  return metricLabelCanvasContext.measureText(text).width
}

/**
 * Минимальные ширины колонок с ReportMetricLabel по самым длинным значениям во всём отчёте.
 * Таблица виртуальная и замеряет ширины только по отрисованным строкам — без этого длинный ROI,
 * появившийся после сортировки/скролла, вылезает за лейбл. Форматируются только min/max значения.
 * @param {Array} rows - корневые строки отчёта (вложенные группы в sg/tg/fg/ffg)
 * @param {Object} total - строка Total
 * @returns {Object} { roi: 96, dss: 104, ... } — только для колонок, где есть значения
 */
export function getMetricLabelMinWidths(rows, total) {
  const keys = Object.keys(METRIC_LABEL_COLUMNS)
  const min = {}
  const max = {}
  const visit = node => {
    keys.forEach(key => {
      const value = node[key]
      if (typeof value !== 'number' || !isFinite(value)) return
      if (!(key in min) || value < min[key]) min[key] = value
      if (!(key in max) || value > max[key]) max[key] = value
    })
    ;[node.sg, node.tg, node.fg, node.ffg].forEach(children => children?.forEach(visit))
  }
  rows?.forEach(visit)
  if (total) visit(total)

  const widths = {}
  keys.forEach(key => {
    if (!(key in min)) return
    const textWidth = Math.max(
      measureMetricLabelText(mutateValueBeforeRender({ value: min[key], key })),
      measureMetricLabelText(mutateValueBeforeRender({ value: max[key], key })),
    )
    widths[key] = Math.ceil(textWidth + METRIC_LABEL_EXTRA_WIDTH + METRIC_LABEL_COLUMNS[key])
  })
  return widths
}

/**
 * Сортирует таблицу по датам, если дата - это первая группировка
 * @param {Object} filters - фильтры
 */
export function checkSortBy(filters){
  const preparedFilters = [
    filters.filter.groupBy,
    filters.filter.secondGroupBy,
    filters.filter.thirdGroupBy,
    filters.filter.fourthGroupBy
  ]
  if(!preparedFilters.some(f => ['date', 'week', 'month'].includes(f))) return
  if (filters.sortBy1 && filters.sortBy1.length) return
  const chosenDateGroupBy = preparedFilters.includes('date')
    ? 'date' : preparedFilters.includes('week') ? 'week' : 'month'
  filters.sortBy = [`g.${chosenDateGroupBy}`]
  filters.sortDesc = [false]
  return {
    sortBy: filters.sortBy,
    sortDesc: filters.sortDesc
  }
}

export function recalcGeneralReportData(item, total, items, percent, percentRowLevel) {
  recalcGeneralReportNode(item, percent)
  walkParents(item, parent => recalcGeneralReportParents(parent, [], percentRowLevel))
  recalcGeneralReportParents(total, items)
}

/**
 * Пересчитывает spend (s) для введенного spend percent (sp) в дочерней ноде GR
 * @param node - дочерняя обновляемая нода
 * @param percent - введенный процент
 */
function recalcGeneralReportNode(node, percent) {
  if(node.s) {
    const previousPercent = 1 + (node.sp ? node.sp / 100 : 0)
    const spendWithoutPercent = node.s / previousPercent
    node.s = spendWithoutPercent + spendWithoutPercent * (percent / 100)
    node.sp = percent
    updateRowData(node)
  } 
}
function recalcGeneralReportParents(node, items = [], percentRowParentLevel = 0) {
  const groupKeys = ['sg', 'tg', 'fg'].slice(0, percentRowParentLevel)
  if(items.length) { // total
    const updatedValues = items.reduce((acc, cur) => {
      acc.s += cur.s
      return acc
    }, {
      s: 0,
    })
    Object.assign(node, updatedValues)
    updateRowData(node)
  } else { // parent row
    groupKeys.forEach(key => {
      if (node[key] && node[key].length) {
        const updatedValues = node[key].reduce((acc, cur) => {
          acc.s += cur.s
          acc.sp += cur.sp
          return acc
        }, {
          s: 0,
          sp: 0,
        })
        updatedValues.sp /= node[key].length
        Object.assign(node, updatedValues)
        updateRowData(node)
      }
    })
  }
}

/**
 * Пересчитывает cFd, ROI, DSS, BSS ряда таблицы GR
 * @param node - объект текущего ряда
 */
function updateRowData(node) {
  node.cfd = node.nd === 0 ? 0 : node.s / node.nd
  node.roi = ((node.i - node.s) / node.s) * 100
  node.dss = node.d / node.s
  node.bss = node.b / node.s
}

/**
 * Сопоставляет названия полей на беке с названиями на фронте
 * @param group - название текущей группировки на беке
 * @returns {*|string} - название группировки на фронте
 */
export function defineGroupId(group) {
  switch (group) {
    case 'traffic_source':
      return 'traffic'
    case 'actual_country':
      return 'geo_country'
    case 'click_id':
      return 'player_id'
    default:
      return group
  }
}

export function toApiGroupBy(groupBy) {
  if (!groupBy) return groupBy
  const match = /^sub_([1-9])$/.exec(groupBy)
  return match ? `sub_id_${match[1]}` : groupBy
}

export function filterBySpend(rows, filter) {
  const { secondGroupBy, thirdGroupBy, fourthGroupBy } = filter

  function hasSpend(item) {
    return item.s !== null && item.s !== undefined && +item.s > 0
  }

  function filterFg(fgItems) {
    return fgItems.filter(hasSpend)
  }

  function filterTg(tgItems) {
    if (fourthGroupBy) {
      return tgItems.reduce((acc, item) => {
        const fg = filterFg(item.fg || [])
        if (fg.length > 0) acc.push({ ...item, fg })
        return acc
      }, [])
    }
    return tgItems.filter(hasSpend)
  }

  function filterSg(sgItems) {
    if (thirdGroupBy) {
      return sgItems.reduce((acc, item) => {
        const tg = filterTg(item.tg || [])
        if (tg.length > 0) acc.push({ ...item, tg })
        return acc
      }, [])
    }
    return sgItems.filter(hasSpend)
  }

  if (secondGroupBy) {
    return rows.reduce((acc, row) => {
      const sg = filterSg(row.sg || [])
      if (sg.length > 0) acc.push({ ...row, sg })
      return acc
    }, [])
  }
  return rows.filter(hasSpend)
}

// Offer-Partners of groups with disabled Offer category come with g.offer_category = null
const EMPTY_GROUP_VALUE = '—'

export function applyGroupFieldFormat(row, groupBy, secondGroupBy = null) {
  if (groupBy === 'offer_category') row.g.offer_category = row.g.offer_category || EMPTY_GROUP_VALUE
  if (groupBy === 'main_manager') {
    row.g.main_manager = `${row.g?.manager_name || ''} ${row.g?.manager_last_name || ''}${
      row.g?.partner_group ? `, ${row.g?.partner_group}` : ''}`
  } else if (groupBy === 'month' || secondGroupBy === 'month') {
    row.g.month = `${row.g.month_title || ''} ${row.g.year || ''}`
  } else if (groupBy === 'promocode') {
    row.g.promocode = row.g.promocode || 'No promocode'
  } else if (groupBy === 'offer') {
    row.g.offer = `${row.g.offer || ''}${row.g.advertiser ? `, ${row.g.advertiser}` : ''}${
      row.g?.country ? `, ${row.g?.country}` : ''}${
      row.g?.product ? `, ${row.g?.product}` : ''}${
      row.g?.payment_model ? `, ${row.g?.payment_model}` : ''}`
  } else if (groupBy === 'site_id') {
    row.g.site_id = `${row.g.site_id || ''}${row.g.advertiser ? `, ${row.g.advertiser}` : ''}${
      row.g?.partner ? `, ${row.g?.partner}` : ''}${
      row.g?.stream ? `, ${row.g?.stream}` : ''}`
  } else if (groupBy === 'partner') {
    row.g.partner = `${row.g.partner || ''}${row.g.partner_tag ? `, ${row.g.partner_tag}` : ''}`
  }
  if (groupBy === 'segment') applySegmentLabel(row)
}

// CPA-3441: null-сегмент — строка «No segment»; флаг нужен, чтобы отправить segment = '' при сохранении Spend percent
export function applySegmentLabel(row) {
  if (row.g.segment) return
  row.g.segment = i18n.tc('generalReport.table.noSegment')
  row.g._segmentNull = true
}

// CPA-3441: текстовый фильтр Segment → segments[] в теле запроса отчёта
export function applySegmentFilter(filter) {
  const segment = typeof filter.segment === 'string' ? filter.segment.trim() : filter.segment
  if (segment) filter.segments = [segment]
  delete filter.segment
  return filter
}

export function prepareNotUsedSpend(notUsedSpendData, firstGroupBy, secondGroupBy, mutateItem = null) {
  const items = Object.values(notUsedSpendData)
  const groupId = defineGroupId(firstGroupBy)
  const subGroupId = defineGroupId(secondGroupBy || 'not_used_spend')

  if (items.length === 1) {
    const item = items[0]
    item.g = { [groupId]: i18n.tc('generalReport.table.notUsedSpend') }
    item.id = generatePassword(8)
    item.sg = []
    item.s = item.price
    item.nd = null
    item.not_used_spend = `${item.country}, ${item.partner}, ${item.stream} (${item.site_id}), ${moment(item.date).format('DD.MM.YYYY')}`
    if (mutateItem) mutateItem(item)
    return item
  }

  const convArr = {
    id: generatePassword(8),
    g: { [groupId]: i18n.tc('generalReport.table.notUsedSpend') },
  }
  convArr.sg = items.map(item => {
    item.g = { [subGroupId]: null }
    item.s = item.price
    item.nd = null
    item.not_used_spend = `${item.country}, ${item.partner}, ${item.stream} (${item.site_id}), ${moment(item.date).format('DD.MM.YYYY')}`
    if (mutateItem) mutateItem(item)
    return item
  })
  convArr.s = items.reduce((acc, item) => acc + +item.price, 0)
  convArr.nd = null
  return convArr
}

export function transformSubRow(subRow, ownGroupBy, crossGroupIds, lowerGroupIds, subIds, opts = {}) {
  const { trafficSuffix = false, conditionalNulling = true, strictIdCheck = true } = opts

  subRow.id = generatePassword(8)

  if (ownGroupBy === 'segment') applySegmentLabel(subRow)

  Object.keys(subRow.g).forEach(key => {
    if (['offer_tag', 'partner_tag', 'offer_category'].includes(key)) return
    const isEmpty = strictIdCheck ? (!key.includes('_id') && !subRow.g[key]) : !subRow.g[key]
    if (isEmpty) {
      subRow.g[key] = '<not selected>'
      subRow.g[key + (trafficSuffix && key === 'traffic' ? '_source_id' : '_id')] = null
    }
  })

  const ownGroupId = defineGroupId(ownGroupBy)
  crossGroupIds.forEach(crossGroupBy => {
    if (!crossGroupBy) return
    const crossId = defineGroupId(crossGroupBy)
    if (!conditionalNulling || crossId !== ownGroupId) subRow.g[crossId] = null
  })

  if (lowerGroupIds.some(id => id && defineGroupId(id) === 'player_id')) subRow.g.click_id = null

  if (ownGroupBy === 'site_id_sub_id') subRow.g.site_id_sub_id = `${subRow.g.site_id} (${subRow.g.sub_id || '-'})`
  if (ownGroupBy === 'sub_id') Object.keys(subRow.g.sub_ids).forEach(key => subIds.add(key))
  if (ownGroupBy === 'offer') subRow.g.offer = `${subRow.g.offer || ''} ${subRow.g.advertiser ? `, ${subRow.g.advertiser}` : ''}`
  if (ownGroupBy === 'offer_category') subRow.g.offer_category = subRow.g.offer_category || EMPTY_GROUP_VALUE
  if (ownGroupBy === 'month') subRow.g.month = `${subRow.g.month_title} ${subRow.g.year}`
  if (ownGroupBy === 'click_id') { subRow.cc = ''; subRow.cr = '' }
}

export function prepareExportReportData(data, filters){ // for report
  const reportData = cloneDeep(data.rows)
  const resString = (groupBy, value) => {
    if(groupBy === 'site_id_sub_id') return `${value.g.site_id} (${value.g.sub_id || '-'})`
    else if (groupBy === 'main_manager') return `${value.g?.manager_name || ''} ${value.g?.manager_last_name || ''}${
      value.g?.partner_group ? `, ${value.g?.partner_group}` : ''}`
    else if (groupBy === 'month') return `${value.g.month_title || ''} ${value.g.year || ''}`
    else if (groupBy === 'date') return value.g.date ? moment(value.g.date).format('DD MMM YYYY') : value
    else if (groupBy === 'stream') return `${value.g.stream || ''} (${value.g.site_id || ''})`
    else if (groupBy === 'promocode') return value.g.promocode || 'No promocode'
    else if (groupBy === 'offer_category') return value.g.offer_category || EMPTY_GROUP_VALUE
    else if (groupBy === 'segment') return value.g.segment || i18n.tc('generalReport.table.noSegment')
    else if (groupBy === 'offer') return `${value.g.offer || ''}${
      value.g.advertiser ? `, ${value.g.advertiser}` : ''
    }${value.g?.country ? `, ${value.g?.country}` : ''}${
      value.g?.product ? `, ${value.g?.product}` : ''
    }${value.g?.payment_model ? `, ${value.g?.payment_model}` : ''}`
    else if (groupBy === 'site_id') return `${value.g.site_id || ''}${
      value.g.advertiser ? `, ${value.g.advertiser}` : ''
    }${value.g?.partner ? `, ${value.g?.partner}` : ''}${
      value.g?.stream ? `, ${value.g?.stream}` : ''
    }`
    else if (groupBy === 'partner') return `${value.g.partner || ''}${value.g.partner_tag ? `, ${value.g.partner_tag}` : ''}`
    else return value.g[groupBy]
  }
  reportData.forEach((row) => { // from fourth to first for not changing a parent value first
    if (row?.sg?.length)
      row.sg.forEach(subRow => {
        if (subRow?.tg?.length)
          subRow.tg.forEach(third => {
            if (third?.fg?.length)
              third.fg.forEach(fourth => {
                fourth.g[filters.filter.groupBy] = resString(filters.filter.groupBy, row)
                fourth.g[filters.filter.secondGroupBy] = resString(filters.filter.secondGroupBy, subRow)
                fourth.g[filters.filter.thirdGroupBy] = resString(filters.filter.thirdGroupBy, third)
                fourth.g[filters.filter.fourthGroupBy] = resString(filters.filter.fourthGroupBy, fourth)
              })
            third.g[filters.filter.groupBy] = resString(filters.filter.groupBy, row)
            third.g[filters.filter.secondGroupBy] = resString(filters.filter.secondGroupBy, subRow)
            third.g[filters.filter.thirdGroupBy] = resString(filters.filter.thirdGroupBy, third)
          })
        subRow.g[filters.filter.groupBy] = resString(filters.filter.groupBy, row)
        subRow.g[filters.filter.secondGroupBy] = resString(filters.filter.secondGroupBy, subRow)
      })
    row.g[filters.filter.groupBy] = resString(filters.filter.groupBy, row)
    if(filters.filter.groupBy === 'click_id') row.g.stream = resString('stream', row)
  })
  return reportData
}